import React from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from './CTAButton';
import { Battery, Shield, TrendingUp, LifeBuoy, Users, Check } from 'lucide-react';
const blocks = [{
  number: '01',
  title: 'The 3-Block Day Map™',
  subtitle: 'Your day. Structured.',
  lines: ['15 min Reset', '5 min Admin', '45 min Relief'],
  close: 'When Reset + Relief happen —\nyou win.',
  icon: Battery,
  color: 'terracotta'
}, {
  number: '02',
  title: 'One Protected Window',
  subtitle: '30 minutes.\nNon-negotiable.',
  lines: ['Messy naps allowed.', 'Your window comes first.'],
  close: null,
  icon: Shield,
  color: 'sage'
}, {
  number: '03',
  title: 'The Gentle Ladder',
  subtitle: 'Gradual crib exposure.\nNo cry-it-out.\nNo panic.',
  lines: ['Small steps.', 'Real progress.'],
  close: null,
  icon: TrendingUp,
  color: 'terracotta'
}, {
  number: '04',
  title: 'The 20-Minute Rescue',
  subtitle: 'When the spiral hits.',
  lines: ['Stabilize.', 'Reset.', 'Continue.'],
  close: null,
  icon: LifeBuoy,
  color: 'sage'
}, {
  number: '05',
  title: 'The Partner Page',
  subtitle: 'One page.\n90 seconds to read.\nClear instructions.',
  lines: ['Support without explaining twice.'],
  close: null,
  icon: Users,
  color: 'terracotta'
}];
export function EditorialTips() {
  return <section id="system" className="relative py-16 md:py-24 px-6 md:px-8 bg-cream-dark overflow-hidden" data-id="element-67">
      {/* Subtle ambient glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-terracotta/4 rounded-full blur-[100px] pointer-events-none" data-id="element-68" />
      <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-sage/4 rounded-full blur-[100px] pointer-events-none" data-id="element-69" />

      <div className="max-w-2xl mx-auto relative z-10" data-id="element-70">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="text-center mb-16 md:mb-20" data-id="element-71">
          <p className="text-xs uppercase tracking-[0.2em] text-terracotta font-bold mb-4" data-id="element-72">
            What You Actually Get
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-brown leading-tight mb-4" data-id="element-73">
            Not theory.
          </h2>
          <p className="font-serif text-2xl md:text-3xl text-brown/60 italic leading-snug" data-id="element-74">
            A structured survival system.
          </p>
        </motion.div>

        {/* Blocks */}
        <div className="space-y-6 md:space-y-8 mb-20" data-id="element-75">
          {blocks.map((block, i) => {
          const Icon = block.icon;
          const isTerracotta = block.color === 'terracotta';
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.8,
            delay: i * 0.1,
            ease: [0.25, 0.1, 0.25, 1]
          }} className="bg-white rounded-2xl p-6 md:p-8 border border-brown/5 relative overflow-hidden" style={{
            boxShadow: '0 4px 24px rgba(61,50,41,0.03), 0 1px 4px rgba(61,50,41,0.02)'
          }} data-id="element-76">
                {/* Top header row: Icon + Number badge */}
                <div className="flex justify-between items-start mb-6" data-id="element-77">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isTerracotta ? 'bg-terracotta/10 text-terracotta' : 'bg-sage/10 text-sage-dark'}`} data-id="element-78">
                    <Icon className="w-6 h-6" data-id="element-79" />
                  </div>
                  <div className="bg-terracotta/5 border border-terracotta/10 text-terracotta text-xs font-bold px-3 py-1 rounded-full tracking-wider" data-id="element-80">
                    {block.number}
                  </div>
                </div>

                {/* Content */}
                <div data-id="element-81">
                  <h3 className="font-serif text-2xl md:text-3xl text-brown font-medium mb-3 leading-tight" data-id="element-82">
                    {block.title}
                  </h3>

                  {/* Subtitle — multiline */}
                  <div className="mb-6" data-id="element-83">
                    {block.subtitle.split('\n').map((line, j) => <p key={j} className="text-brown-light text-base md:text-lg leading-relaxed" data-id="element-84">
                        {line}
                      </p>)}
                  </div>

                  {/* Lines with Check icons */}
                  <div className="space-y-3 mb-6" data-id="element-85">
                    {block.lines.map((line, j) => <div key={j} className="flex items-start gap-3" data-id="element-86">
                        <Check className="w-5 h-5 text-sage shrink-0 mt-0.5" data-id="element-87" />
                        <p className="text-brown text-base font-medium leading-snug" data-id="element-88">
                          {line}
                        </p>
                      </div>)}
                  </div>

                  {/* Close quote */}
                  {block.close && <div className="mt-6 pt-6 border-t border-brown/5" data-id="element-89">
                      <div className="pl-4 border-l-2 border-terracotta/30" data-id="element-90">
                        {block.close.split('\n').map((line, j) => <p key={j} className="font-serif italic text-brown/70 text-lg leading-relaxed" data-id="element-91">
                            {line}
                          </p>)}
                      </div>
                    </div>}
                </div>
              </motion.div>;
        })}
        </div>

        {/* Closing micro line */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="text-center mb-10" data-id="element-92">
          <div className="w-12 h-px bg-terracotta/30 mx-auto mb-8" data-id="element-93" />
          <p className="font-serif text-xl md:text-2xl text-brown/60 italic tracking-wide" data-id="element-94">
            Simple. Structured. Doable.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.1,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="text-center" data-id="element-95">
          <p className="font-serif text-lg md:text-xl text-brown/70 italic mb-8 leading-relaxed" data-id="element-96">
            Everything you need to reclaim your day — in one calm PDF.
          </p>

          <CTAButton text="I'm Ready for Relief" className="min-w-[300px]" data-id="element-97" />

          <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-xs text-brown-light/50" data-id="element-98">
            <span className="flex items-center gap-1.5" data-id="element-99">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-100">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-101" />
                <polyline points="7 10 12 15 17 10" data-id="element-102" />
                <line x1="12" y1="15" x2="12.01" y2="18" data-id="element-103" />
              </svg>
              Instant PDF
            </span>
            <span className="text-brown-light/20" data-id="element-104">·</span>
            <span className="flex items-center gap-1.5" data-id="element-105">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-106">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2" data-id="element-107" />
                <line x1="12" y1="18" x2="12.01" y2="18" data-id="element-108" />
              </svg>
              Phone-friendly
            </span>
            <span className="text-brown-light/20" data-id="element-109">·</span>
            <span className="flex items-center gap-1.5" data-id="element-110">
              <svg className="w-3.5 h-3.5 text-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-111">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-112" />
                <polyline points="9 12 11 14 15 10" data-id="element-113" />
              </svg>
              14-day guarantee
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}